import { createApi } from '@reduxjs/toolkit/query/react';
import baseQueryWithTokenManagement from 'pages/auth/apiSlice';
import moment from 'moment';

export const itemApi = createApi({
  tagTypes: ['ITEM_LIST', 'ITEM_TAGS', 'ITEM', 'ITEM_SUPPLIER_LIST', 'ITEM_SELECTOR_LIST'],
  reducerPath: 'itemApi',
  baseQuery: baseQueryWithTokenManagement,
  endpoints: (builder) => ({
    itemList: builder.query<any, any>({
      query: (args) => {
        const { size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/item/api/item-management/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        const content = response?.data?.content[0] || {};
        const { brands = {}, suppliers = {} } = content;

        if (content.items.length) {
          content.items = content.items.map((item: any) => ({
            ...item,
            brand: brands[item.brandId],
            suppliers: brands[item.brandId]?.supplierIds?.map((id: string) => suppliers[id]) || [],
            formattedLastModifiedDate: {
              full: moment(item.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.lastModifiedDate).format('DD.MM.YYYY'),
            },
            formattedCreatedDate: {
              full: moment(item.createDate).format('DD.MM.YYYY HH:mm:ss'),
              day: moment.utc(item.createDate).format('DD.MM.YYYY'),
            },
          }));
        }
        return response.data;
      },
      providesTags: () => ['ITEM_LIST'],
    }),
    itemSupplierList: builder.query<any, any>({
      query: (args) => {
        const { itemId, size = 10, page = 1, sortField, sortOrder, search, filters } = args;
        const number = page - 1;
        let sortParam = '';
        if (sortField && sortOrder) {
          sortParam = `&sort=${sortField},${sortOrder}`;
        }
        const params = { ...filters, ...(typeof search !== 'undefined' && { search }) };
        return {
          url: `/wrd/api/item-management/${itemId}/suppliers/list?size=${size}&page=${number}${sortParam}`,
          params: Object.keys(params).length ? params : undefined,
        };
      },
      transformResponse: (response: any) => {
        response.data.content = (response?.data?.content || []).map((item: any) => ({
          ...item,
          formattedExpiryDate: moment.utc(item.expiryDate).format('DD.MM.YYYY'),
          formattedCreateDate: moment.utc(item.createDate).format('DD.MM.YYYY'),
          key: item.supplier?.id,
          id: item.supplier?.id,
        }));
        return response.data;
      },
      providesTags: () => ['ITEM_SUPPLIER_LIST'],
    }),
    itemById: builder.query<any, any>({
      query: (args) => {
        const { id } = args;
        return {
          url: `/item/api/item-management/get-by-id?id=${id}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return {
          ...response.data,
          formattedLastModifiedDate: moment(response.data.lastModifiedDate).format('DD.MM.YYYY HH:mm:ss'),
          formattedCreatedDate: moment(response.data.createDate).format('DD.MM.YYYY HH:mm:ss'),
        };
      },
      providesTags: () => ['ITEM'],
    }),
    itemTags: builder.query<any, void>({
      query: () => ({
        url: '/item/api/item-management/get-tags',
      }),
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        return response?.data || [];
      },
      providesTags: () => ['ITEM_TAGS'],
    }),
    itemCreate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/item/api/item-management`,
          method: 'post',
          body,
        };
      },
      invalidatesTags: () => ['ITEM_LIST', 'ITEM_SELECTOR_LIST'],
    }),
    itemUpdate: builder.mutation<any, any>({
      query: (body) => {
        return {
          url: `/item/api/item-management`,
          method: 'put',
          body,
        };
      },
      invalidatesTags: () => ['ITEM_LIST', 'ITEM', 'ITEM_SUPPLIER_LIST'],
    }),
    itemSelectorList: builder.query<any, any>({
      query: (brandId) => {
        const params = brandId ? `?brandId=${brandId?.brandId || brandId}` : '';
        return {
          url: `/item/api/item-management/selector-list/skus${params}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.sku,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['ITEM_SELECTOR_LIST'],
    }),
    itemSelectorListMultiple: builder.query<any, any>({
      query: (args) => {
        const params = args?.brandIds?.length ? `?brandIds=${args.brandIds}` : '';
        return {
          url: `/item/api/item-management/selector-list/skus/multiple${params}`,
        };
      },
      transformResponse: (response: any) => {
        // Transform the response to the desired shape
        response.data = (response?.data || []).map((item: any) => ({
          ...item,
          label: item.sku,
          value: item.id,
        }));
        return response.data;
      },
      providesTags: () => ['ITEM_SELECTOR_LIST'],
    }),
  }),
});
export const {
  useItemListQuery,
  useItemCreateMutation,
  useItemByIdQuery,
  useItemUpdateMutation,
  useItemTagsQuery,
  useItemSupplierListQuery,
  useItemSelectorListQuery,
  useItemSelectorListMultipleQuery,
} = itemApi;
